/**
 * Analysis History Page
 * Lists all past communication analyses with pagination
 */

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Layout } from '@/components/layout/Layout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorDisplay } from '@/components/common/ErrorDisplay';
import { analysisApi } from '@/services/api';

interface AnalysisListItem {
  id: string;
  date: string;
  text_snippet: string;
  title?: string;
  context?: string;
  sentiment: {
    positivity: number;
    negativity: number;
    neutral: number;
    toxicity: number;
  };
  emotion: {
    [key: string]: number;
  };
  url: string;
  metadata?: {
    source?: 'email' | 'dashboard';
    subject?: string;
    original_date?: string;
    forwarded_date?: string;
  };
}

const AnalysisHistory: React.FC = () => {
  const [page, setPage] = useState(1);
  const perPage = 10;

  // Fetch analyses list
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['analyses', page],
    queryFn: async () => {
      const response = await analysisApi.getAnalyses({ page, per_page: perPage });
      return response.data?.data;
    },
    staleTime: 60 * 1000, // 1 minute
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
  });

  const getSentimentColor = (sentiment: { positivity: number; negativity: number; neutral: number }) => {
    const max = Math.max(sentiment.positivity, sentiment.negativity, sentiment.neutral);
    if (max === sentiment.positivity) return 'bg-green-100 text-green-700';
    if (max === sentiment.negativity) return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getSentimentLabel = (sentiment: { positivity: number; negativity: number; neutral: number }) => {
    const max = Math.max(sentiment.positivity, sentiment.negativity, sentiment.neutral);
    if (max === sentiment.positivity) return 'Positive';
    if (max === sentiment.negativity) return 'Negative';
    return 'Neutral';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Analysis History
          </h1>
          <p className="text-sm sm:text-base text-gray-600">
            View and review your past communication analyses
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center py-12">
            <LoadingSpinner 
              size="lg" 
              message="Loading analyses..." 
              showTimeoutMessage={true}
            />
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="flex justify-center">
            <ErrorDisplay
              type="network"
              title="Unable to Load History"
              message="We couldn't load your analysis history. This might be due to a connection issue."
              onRetry={() => refetch()}
              showRetry={true}
              showGoBack={false}
            />
          </div>
        )}

        {/* Empty State */}
        {!isLoading && !error && data?.analyses?.length === 0 && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-8 sm:p-12 text-center">
            <span className="material-symbols-outlined text-gray-400 text-5xl sm:text-6xl mb-4">
              search_off
            </span>
            <h2 className="text-xl sm:text-2xl font-semibold text-gray-900 mb-2">
              No Analyses Yet
            </h2>
            <p className="text-sm sm:text-base text-gray-600 mb-6">
              You haven't analysed any communications yet. Start your first analysis!
            </p>
            <Link
              to="/analyse"
              className="inline-flex items-center px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors font-medium"
            >
              <span className="material-symbols-outlined mr-2">
                psychology
              </span>
              Analyse Communication
            </Link>
          </div>
        )}

        {/* Analyses List */}
        {!isLoading && !error && data?.analyses && data.analyses.length > 0 && (
          <>
            <div className="space-y-4">
              {data.analyses.map((analysis: AnalysisListItem) => (
                <Link
                  key={analysis.id}
                  to={`/analysis/${analysis.id}`}
                  className="block bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md hover:border-primary-300 transition-all"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        {/* Sentiment Badge */}
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getSentimentColor(analysis.sentiment)}`}>
                          {getSentimentLabel(analysis.sentiment)}
                        </span>

                        {/* Email Source Badge */}
                        {analysis.metadata?.source === 'email' && (
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-700">
                            <span className="material-symbols-outlined text-sm">
                              mail
                            </span>
                            Email
                          </span>
                        )}

                        {/* Context Badge */}
                        {analysis.context && (
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                            {analysis.context}
                          </span>
                        )}

                        {/* Date */}
                        <span className="text-sm text-gray-500">
                          {formatDate(analysis.date)}
                        </span>
                      </div>

                      {/* Email Subject (if available) */}
                      {analysis.metadata?.source === 'email' && analysis.metadata?.subject && (
                        <div className="flex items-center gap-2 mb-2">
                          <span className="material-symbols-outlined text-sm text-gray-400">
                            subject
                          </span>
                          <span className="text-sm text-gray-600 italic">
                            {analysis.metadata.subject}
                          </span>
                        </div>
                      )}

                      {/* Title */}
                      {analysis.title && (
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {analysis.title}
                        </h3>
                      )}

                      {/* Text Preview */}
                      <p className="text-gray-600 leading-relaxed">
                        {analysis.text_snippet}
                      </p>
                    </div>

                    {/* Arrow Icon */}
                    <span className="material-symbols-outlined text-gray-400 ml-4">
                      chevron_right
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {/* Pagination */}
            {data.pagination && data.pagination.pages > 1 && (
              <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row items-center justify-between gap-4 border-t border-gray-200 pt-4 sm:pt-6">
                <div className="text-xs sm:text-sm text-gray-600 text-center sm:text-left">
                  Showing page {data.pagination.page} of {data.pagination.pages}
                  {' '}({data.pagination.total} total)
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">
                      chevron_left
                    </span>
                  </button>

                  <button
                    onClick={() => setPage(Math.min(data.pagination.pages, page + 1))}
                    disabled={page === data.pagination.pages}
                    className="px-3 sm:px-4 py-2 text-sm sm:text-base bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    <span className="material-symbols-outlined text-sm">
                      chevron_right
                    </span>
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* New Analysis Button (FAB style) */}
        <Link
          to="/analyse"
          className="fixed bottom-8 right-8 flex items-center justify-center w-14 h-14 bg-primary-500 text-white rounded-full shadow-lg hover:bg-primary-600 hover:shadow-xl transition-all"
          title="New Analysis"
        >
          <span className="material-symbols-outlined text-2xl">
            add
          </span>
        </Link>
      </div>
    </Layout>
  );
};

export default AnalysisHistory;

